/**
 * Taxonomy Fallback Data
 *
 * This file contains hardcoded fallback data for problematic layer/category combinations.
 * It provides a last resort when the standard taxonomy data structures fail to provide
 * valid subcategories.
 */

import { TaxonomyItem } from '../types/taxonomy.types';

// L layer subcategories
export const L_SUBCATEGORIES: Record<string, string[]> = {
  PRF: [
    'PRF.BAS',
    'PRF.LEO',
    'PRF.SEQ',
    'PRF.LED',
    'PRF.ATH',
    'PRF.MIN',
    'PRF.SPK',
  ],
  TRD: ['TRD.BAS', 'TRD.KIM', 'TRD.SAR', 'TRD.KLT', 'TRD.HAN', 'TRD.CHG'],
  STG: [
    'STG.BAS',
    'STG.BRF',
    'STG.RLE',
    'STG.GCK',
    'STG.WBL',
    'STG.CGR',
    'STG.TBZ',
    'STG.NEO',
    'STG.MTL',
  ],
  CAS: ['CAS.BAS', 'CAS.STR', 'CAS.DNM', 'CAS.ATL', 'CAS.BOH', 'CAS.MIN'],
  DSG: ['DSG.BAS', 'DSG.COU', 'DSG.RUN', 'DSG.LUX', 'DSG.HFA', 'DSG.BSP'],
  URB: ['URB.BAS', 'URB.HIP', 'URB.GRF', 'URB.TEC', 'URB.SKT', 'URB.PNK'],
  VIN: ['VIN.BAS', 'VIN.SIX', 'VIN.SEV', 'VIN.EGT', 'VIN.NIN', 'VIN.RET'],
  CUL: ['CUL.BAS', 'CUL.AFR', 'CUL.IND', 'CUL.MDE', 'CUL.LAT', 'CUL.ASN'],
  FUT: ['FUT.BAS', 'FUT.CYB', 'FUT.SPC', 'FUT.HOL', 'FUT.TEC', 'FUT.SCI'],
  FAN: ['FAN.BAS', 'FAN.MED', 'FAN.FAI', 'FAN.MYT', 'FAN.MAG', 'FAN.ELV'],
  Y2K: ['Y2K.BAS', 'Y2K.E2K', 'Y2K.L9T', 'Y2K.MIL', 'Y2K.POP', 'Y2K.BOY'],
  SUS: ['SUS.BAS', 'SUS.UPC', 'SUS.ECO', 'SUS.MIN', 'SUS.NAT', 'SUS.VRE'],
  DIG: ['DIG.BAS', 'DIG.NFT', 'DIG.DON', 'DIG.ARF', 'DIG.VCO', 'DIG.W3S'],
  SPO: ['SPO.BAS', 'SPO.TRK', 'SPO.ATH', 'SPO.GYM'],
};

// S layer subcategories
export const S_SUBCATEGORIES: Record<string, string[]> = {
  POP: [
    'POP.BAS',
    'POP.DIV',
    'POP.IDF',
    'POP.LGF',
    'POP.LGM',
    'POP.ICM',
    'POP.HPM',
    'POP.GLB',
    'POP.TEN',
    'POP.DNC',
    'POP.ELC',
    'POP.DRM',
    'POP.IND',
    'POP.SOU',
    'POP.PNK',
    'POP.IDP',
  ],
  RCK: [
    'RCK.BAS',
    'RCK.RSF',
    'RCK.RSM',
    'RCK.CLS',
    'RCK.MOD',
    'RCK.MTL',
    'RCK.PNK',
    'RCK.ALT',
    'RCK.GRG',
    'RCK.PRG',
    'RCK.IND',
    'RCK.PSY',
    'RCK.BLU',
  ],
  HIP: [
    'HIP.BAS',
    'HIP.HPF',
    'HIP.RAP',
    'HIP.TRP',
    'HIP.MCS',
    'HIP.DRL',
    'HIP.PRD',
    'HIP.MUM',
  ],
  RNB: [
    'RNB.BAS',
    'RNB.SOU',
    'RNB.MOD',
    'RNB.NEO',
    'RNB.GOS',
    'RNB.BLU',
    'RNB.FNK',
    'RNB.JSU',
    'RNB.URB',
    'RNB.ALT',
    'RNB.FSU',
  ],
  DNC: [
    'DNC.BAS',
    'DNC.PRD',
    'DNC.HSE',
    'DNC.TEC',
    'DNC.TRN',
    'DNC.DUB',
    'DNC.FUT',
    'DNC.DNB',
    'DNC.AMB',
    'DNC.LIV',
    'DNC.EXP',
  ],
  LAT: [
    'LAT.BAS',
    'LAT.REG',
    'LAT.POP',
    'LAT.SAL',
    'LAT.BCH',
    'LAT.RCK',
    'LAT.TRP',
    'LAT.MER',
    'LAT.JZZ',
    'LAT.CUM',
    'LAT.FLM',
  ],
  IND: [
    'IND.BAS',
    'IND.RCK',
    'IND.POP',
    'IND.FLK',
    'IND.ELC',
    'IND.HIP',
    'IND.DRM',
    'IND.ALT',
    'IND.PSY',
    'IND.CHL',
    'IND.PRP',
  ],
  ALT: [
    'ALT.BAS',
    'ALT.RCK',
    'ALT.ELC',
    'ALT.HIP',
    'ALT.FLK',
    'ALT.MTL',
    'ALT.PNK',
    'ALT.GRG',
    'ALT.EMO',
    'ALT.EXP',
    'ALT.CHL',
  ],
  WLD: [
    'WLD.BAS',
    'WLD.FUS',
    'WLD.AFR',
    'WLD.IND',
    'WLD.MDE',
    'WLD.CEL',
    'WLD.REG',
    'WLD.ASN',
    'WLD.BAL',
    'WLD.MED',
    'WLD.GLB',
  ],
  JZZ: [
    'JZZ.BAS',
    'JZZ.CON',
    'JZZ.TRD',
    'JZZ.SMO',
    'JZZ.EXP',
    'JZZ.LAT',
    'JZZ.WLD',
  ],
  JPO: [
    'JPO.BAS',
    'JPO.POP',
    'JPO.RKF',
    'JPO.ANI',
    'JPO.IDL',
    'JPO.ALT',
    'JPO.ELC',
    'JPO.HIP',
    'JPO.ROK',
  ],
  BOL: [
    'BOL.BAS',
    'BOL.CLS',
    'BOL.MOD',
    'BOL.ROM',
    'BOL.PDP',
    'BOL.FLK',
    'BOL.DVT',
    'BOL.GHZ',
    'BOL.SFI',
  ],
  KPO: [
    'KPO.BAS',
    'KPO.IDL',
    'KPO.BOY',
    'KPO.GRL',
    'KPO.HIP',
    'KPO.ROK',
    'KPO.RND',
    'KPO.BAL',
    'KPO.ELC',
  ],
  VAV: [
    'VAV.BAS',
    'VAV.POP',
    'VAV.RCK',
    'VAV.HIP',
    'VAV.ELC',
    'VAV.WRL',
    'VAV.IDL',
    'VAV.IND',
    'VAV.ALT',
  ],
  CUL: [
    'CUL.BAS',
    'CUL.HIS',
    'CUL.ICN',
    'CUL.SYM',
    'CUL.MYT',
    'CUL.FOL',
    'CUL.TRD',
    'CUL.REL',
    'CUL.CEL',
  ],
  FAN: [
    'FAN.BAS',
    'FAN.MAG',
    'FAN.ELF',
    'FAN.DRG',
    'FAN.SPA',
    'FAN.VMP',
    'FAN.MYT',
    'FAN.WIZ',
    'FAN.DDW',
  ],
};

// G layer subcategories
export const G_SUBCATEGORIES: Record<string, string[]> = {
  POP: [
    'POP.BAS',
    'POP.FEM',
    'POP.MAL',
    'POP.KID',
    'POP.SEN',
    'POP.GRP',
    'POP.DIV',
    'POP.IDL',
    'POP.TNS',
    'POP.PAR',
    'POP.YOU',
    'POP.ADU',
  ],
  NAT: [
    'NAT.BAS',
    'NAT.AFR',
    'NAT.EUR',
    'NAT.NAM',
    'NAT.SAM',
    'NAT.ASI',
    'NAT.MSA',
    'NAT.CAR',
    'NAT.OCE',
    'NAT.MIX',
  ],
  CUS: [
    'CUS.BAS',
    'CUS.BUS',
    'CUS.URB',
    'CUS.STR',
    'CUS.RUL',
    'CUS.SUR',
    'CUS.DES',
    'CUS.FUM',
    'CUS.SPR',
    'CUS.FUC',
    'CUS.PHA',
    'CUS.ARC',
  ],
  SCF: [
    'SCF.BAS',
    'SCF.CYB',
    'SCF.AGF',
    'SCF.ANG',
    'SCF.SPC',
    'SCF.MUT',
    'SCF.DBR',
    'SCF.BLP',
    'SCF.BEU',
    'SCF.SYN',
    'SCF.AND',
    'SCF.DYS',
  ],
  FAN: [
    'FAN.BAS',
    'FAN.BFN',
    'FAN.HRO',
    'FAN.ELF',
    'FAN.DWF',
    'FAN.WIZ',
    'FAN.DRG',
    'FAN.ANG',
    'FAN.DMN',
    'FAN.MYT',
    'FAN.FST',
    'FAN.FLK',
  ],
  OCP: [
    'OCP.BAS',
    'OCP.TEC',
    'OCP.SCI',
    'OCP.EDU',
    'OCP.MED',
    'OCP.LAW',
    'OCP.FIN',
    'OCP.ART',
    'OCP.MUS',
    'OCP.SPO',
    'OCP.ENT',
    'OCP.TRD',
  ],
  CUL: [
    'CUL.BAS',
    'CUL.TRB',
    'CUL.IND',
    'CUL.AFR',
    'CUL.ASN',
    'CUL.EUR',
    'CUL.NAM',
    'CUL.SAM',
    'CUL.OCE',
    'CUL.MDE',
    'CUL.CAR',
    'CUL.VKG',
  ],
  SPM: [
    'SPM.BAS',
    'SPM.WRR',
    'SPM.GDN',
    'SPM.KPN',
    'SPM.DFS',
    'SPM.SUP',
    'SPM.SIF',
    'SPM.NOF',
    'SPM.BOF',
    'SPM.COF',
    'SPM.AOE',
    'SPM.RTS',
  ],
  ANM: [
    'ANM.BAS',
    'ANM.CAT',
    'ANM.DOG',
    'ANM.BRD',
    'ANM.HSE',
    'ANM.DIN',
    'ANM.AQT',
    'ANM.INS',
    'ANM.FRM',
    'ANM.MYT',
    'ANM.WLD',
    'ANM.ROD',
  ],
  HIS: [
    'HIS.BAS',
    'HIS.ANC',
    'HIS.MED',
    'HIS.REN',
    'HIS.COL',
    'HIS.VIC',
    'HIS.EDW',
    'HIS.WW1',
    'HIS.WW2',
    'HIS.MOD',
    'HIS.CON',
    'HIS.MER',
  ],
  ROL: [
    'ROL.BAS',
    'ROL.PRO',
    'ROL.DEF',
    'ROL.WRR',
    'ROL.HLR',
    'ROL.SUP',
    'ROL.CON',
    'ROL.DPS',
    'ROL.TNK',
    'ROL.LEA',
    'ROL.FOL',
    'ROL.CHA',
  ],
  NFT: [
    'NFT.BAS',
    'NFT.ART',
    'NFT.PNK',
    'NFT.APE',
    'NFT.PXL',
    'NFT.MTF',
    'NFT.VSL',
    'NFT.LUM',
    'NFT.AUD',
    'NFT.PHY',
    'NFT.PRO',
    'NFT.MTA',
  ],
};

// Pre-built TaxonomyItem objects for common problematic combinations
export const FALLBACK_SUBCATEGORIES: Record<
  string,
  Record<string, TaxonomyItem[]>
> = {
  L: {
    PRF: [
      { id: 1, code: 'BAS', numericCode: '001', name: 'Base' },
      { id: 2, code: 'LEO', numericCode: '002', name: 'Leotard' },
      { id: 3, code: 'SEQ', numericCode: '003', name: 'Sequined' },
      { id: 4, code: 'LED', numericCode: '004', name: 'LED' },
      { id: 5, code: 'ATH', numericCode: '005', name: 'Athletic' },
      { id: 6, code: 'MIN', numericCode: '006', name: 'Minimalist' },
      { id: 7, code: 'SPK', numericCode: '007', name: 'Sparkly_Dress' },
    ],
    TRD: [
      { id: 1, code: 'BAS', numericCode: '001', name: 'Base' },
      { id: 2, code: 'KIM', numericCode: '002', name: 'Kimono' },
      { id: 3, code: 'SAR', numericCode: '003', name: 'Saree' },
      { id: 4, code: 'KLT', numericCode: '004', name: 'Kilt' },
      { id: 5, code: 'HAN', numericCode: '005', name: 'Hanbok' },
      { id: 6, code: 'CHG', numericCode: '006', name: 'Cheongsam' },
    ],
  },
  S: {
    DNC: [
      { id: 1, code: 'BAS', numericCode: '001', name: 'Base' },
      { id: 2, code: 'PRD', numericCode: '002', name: 'Producer' },
      { id: 3, code: 'HSE', numericCode: '003', name: 'House' },
      { id: 4, code: 'TEC', numericCode: '004', name: 'Techno' },
      { id: 5, code: 'TRN', numericCode: '005', name: 'Trance' },
      { id: 6, code: 'DUB', numericCode: '006', name: 'Dubstep' },
      { id: 7, code: 'FUT', numericCode: '007', name: 'Future_Bass' },
      { id: 8, code: 'DNB', numericCode: '008', name: 'Drum_n_Bass' },
      { id: 9, code: 'AMB', numericCode: '009', name: 'Ambient' },
      { id: 10, code: 'LIV', numericCode: '010', name: 'Live_Electronic' },
      { id: 11, code: 'EXP', numericCode: '011', name: 'Experimental' },
    ],
    POP: [
      { id: 1, code: 'BAS', numericCode: '001', name: 'Base' },
      { id: 2, code: 'DIV', numericCode: '002', name: 'Diva' },
      { id: 3, code: 'IDF', numericCode: '003', name: 'Indie Female' },
      { id: 4, code: 'LGF', numericCode: '004', name: 'LGBTQ+ Female' },
      { id: 5, code: 'LGM', numericCode: '005', name: 'LGBTQ+ Male' },
      { id: 6, code: 'ICM', numericCode: '006', name: 'Icon Male' },
      { id: 7, code: 'HPM', numericCode: '007', name: 'Hipster Male' },
      { id: 8, code: 'GLB', numericCode: '008', name: 'Global' },
      { id: 9, code: 'TEN', numericCode: '009', name: 'Teen' },
      { id: 10, code: 'DNC', numericCode: '010', name: 'Dance' },
      { id: 11, code: 'ELC', numericCode: '011', name: 'Electronic' },
    ],
    HIP: [
      { id: 1, code: 'BAS', numericCode: '001', name: 'Base' },
      { id: 2, code: 'HPF', numericCode: '002', name: 'Hip-Hop Female' },
      { id: 3, code: 'RAP', numericCode: '003', name: 'Rapper' },
      { id: 4, code: 'TRP', numericCode: '004', name: 'Trap' },
      { id: 5, code: 'MCS', numericCode: '005', name: 'MC Style' },
      { id: 6, code: 'DRL', numericCode: '006', name: 'Drill' },
      { id: 7, code: 'PRD', numericCode: '007', name: 'Producer' },
      { id: 8, code: 'MUM', numericCode: '008', name: 'Mumble' },
    ],
  },
  W: {
    BCH: [
      { id: 1, code: 'BAS', numericCode: '001', name: 'Base' },
      { id: 2, code: 'TRO', numericCode: '002', name: 'Tropical' },
      { id: 3, code: 'ISL', numericCode: '003', name: 'Island' },
      { id: 3, code: 'SUN', numericCode: '003', name: 'Sunset' },
      { id: 4, code: 'SRF', numericCode: '004', name: 'Surf' },
      { id: 5, code: 'MAR', numericCode: '005', name: 'Marine' },
    ],
    UTB: [
      { id: 1, code: 'BAS', numericCode: '001', name: 'Base' },
      { id: 2, code: 'CIT', numericCode: '002', name: 'City' },
      { id: 3, code: 'STR', numericCode: '003', name: 'Street' },
      { id: 4, code: 'NGT', numericCode: '004', name: 'Night' },
      { id: 5, code: 'SUB', numericCode: '005', name: 'Suburb' },
      { id: 6, code: 'IND', numericCode: '006', name: 'Industrial' },
    ],
  },
};
